import React from 'react';
import { useParams } from 'react-router';

import { Spinner } from '@admin/components/spinner';
import { facilitiesURL } from '@admin/config/routes';
import { usePermissionKey } from '@admin/hooks/use_permission';
import { useTitle } from '@admin/hooks/use_title';
import { client } from '@admin/libraries/apollo';
import {
  FacilityServiceTypeDocument,
  FacilityServiceTypeUpdateInput,
  useFacilityServiceTypeQuery,
  useFacilityServiceTypeUpdateMutation,
} from '@admin/schema';
import { BreadcrumbCurrent } from '@admin/styled/breadcrumb_current';
import { BreadcrumbNav } from '@admin/styled/breadcrumb_nav';
import { BreadcrumbSep } from '@admin/styled/breadcrumb_sep';
import { Button } from '@admin/styled/button';
import { ButtonLink } from '@admin/styled/button_link';
import { Card } from '@admin/styled/card';
import { CardToolbar } from '@admin/styled/card_toolbar';
import { CodeText } from '@admin/styled/code_text';
import { DetailGrid } from '@admin/styled/detail_grid';
import { DetailItem } from '@admin/styled/detail_item';
import { DetailLabel } from '@admin/styled/detail_label';
import { DetailValue } from '@admin/styled/detail_value';
import { ErrorBanner } from '@admin/styled/error_banner';
import { MutedPanel } from '@admin/styled/muted_panel';
import { MutedText } from '@admin/styled/muted_text';
import { Page } from '@admin/styled/page';
import { PageHeader } from '@admin/styled/page_header';
import { PageTitle } from '@admin/styled/page_title';
import { Section } from '@admin/styled/section';
import { SectionActions } from '@admin/styled/section_actions';
import { SectionTitle } from '@admin/styled/section_title';
import { SlugChip } from '@admin/styled/slug_chip';

import { ServiceTypeForm } from './service_type_form';

type FacilityServiceTypeDetailsProps = {
  startInEditMode?: boolean;
};

export const FacilityServiceTypeDetails: React.FC<FacilityServiceTypeDetailsProps> = ({ startInEditMode = false }) => {
  const { id } = useParams<{ id: string }>();
  const canUpdate = usePermissionKey('facility:update');
  const [editing, setEditing] = React.useState(startInEditMode);
  const [error, setError] = React.useState<string | undefined>();

  if (!id) {
    throw new Error('undefined ID');
  }

  const { data, loading } = useFacilityServiceTypeQuery({
    client,
    variables: { id },
  });

  const [update, { loading: saving }] = useFacilityServiceTypeUpdateMutation({ client });

  const serviceType = data?.facilityServiceType;
  useTitle(serviceType?.name ?? 'Service Type');

  if (loading && !data) {
    return <Spinner />;
  }

  if (!serviceType) {
    return (
      <Page>
        <MutedPanel>Service type not found.</MutedPanel>
      </Page>
    );
  }

  const onSubmit = async (input: FacilityServiceTypeUpdateInput) => {
    setError(undefined);
    const result = await update({
      variables: { id: serviceType.id, input },
      update(cache, { data: mutationData }) {
        const mutationPayload = mutationData?.facilityServiceTypeUpdate;
        if (mutationPayload?.status !== 'OK' || !mutationPayload.facilityServiceType) {
          return;
        }

        cache.writeQuery({
          query: FacilityServiceTypeDocument,
          variables: { id: serviceType.id },
          data: { facilityServiceType: mutationPayload.facilityServiceType },
        });
      },
    });
    const payload = result.data?.facilityServiceTypeUpdate;

    if (payload?.error) {
      setError(payload.error);
      return;
    }

    if (payload?.status === 'OK') {
      setEditing(false);
    }
  };

  return (
    <Page>
      <BreadcrumbNav aria-label="Breadcrumb">
        <ButtonLink to={facilitiesURL()}>Facilities</ButtonLink>
        <BreadcrumbSep>/</BreadcrumbSep>
        <BreadcrumbCurrent>{serviceType.name}</BreadcrumbCurrent>
      </BreadcrumbNav>

      <PageHeader>
        <PageTitle>{serviceType.name}</PageTitle>
      </PageHeader>

      {error && <ErrorBanner role="alert">{error}</ErrorBanner>}

      <Section>
        {editing && canUpdate ? (
          <ServiceTypeForm
            initialValues={{
              slug: serviceType.slug,
              name: serviceType.name,
              description: serviceType.description,
              position: serviceType.position,
            }}
            onSubmit={onSubmit}
            onCancel={() => {
              setEditing(false);
              setError(undefined);
            }}
            saving={saving}
          />
        ) : (
          <Card>
            <CardToolbar>
              <SectionTitle>Overview</SectionTitle>
              {canUpdate && (
                <SectionActions>
                  <Button variant="secondary" size="sm" type="button" onClick={() => setEditing(true)}>
                    Edit details
                  </Button>
                </SectionActions>
              )}
            </CardToolbar>
            <DetailGrid>
              <DetailItem>
                <DetailLabel>Slug</DetailLabel>
                <DetailValue>
                  <SlugChip>{serviceType.slug}</SlugChip>
                </DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Display name</DetailLabel>
                <DetailValue>{serviceType.name}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Sort order</DetailLabel>
                <DetailValue>
                  <CodeText>{serviceType.position}</CodeText>
                </DetailValue>
              </DetailItem>
              <DetailItem style={{ gridColumn: '1 / -1' }}>
                <DetailLabel>Description</DetailLabel>
                <DetailValue>
                  {serviceType.description.trim() ? serviceType.description : <MutedText>Not set</MutedText>}
                </DetailValue>
              </DetailItem>
            </DetailGrid>
          </Card>
        )}
      </Section>
    </Page>
  );
};

export const EditFacilityServiceType: React.FC = () => <FacilityServiceTypeDetails startInEditMode />;
